import React, { useEffect, useState } from "react";
import { Modal, Button, Input } from "antd";
import { toast } from "react-toastify";

// Confirm-with-email-OTP modal for sensitive admin actions.
//
//   rows        [[label, value], ...] summarising exactly what will change
//   warning     optional red call-out shown above the summary
//   onSendOtp   () => Promise<{ data: { expiresInSec, resendAfterSec } }>
//   onConfirm   (otp) => Promise  — resolve on success (parent then closes the
//               modal by setting `open` to false); reject with the API error
//   confirmText label of the final button
//
// Flow: Send OTP -> 6-digit input with expiry + resend countdowns -> Confirm.
// Wrong / expired / used OTP errors show inline next to the code box.
const AdminOtpModal = ({ open, title, rows = [], warning, danger = false, confirmText = "Confirm", onSendOtp, onConfirm, onClose }) => {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  // Fresh state every time the modal opens or closes.
  useEffect(() => {
    setOtpSent(false);
    setOtp("");
    setOtpError("");
    setSending(false);
    setConfirming(false);
    setExpiresAt(0);
    setResendAt(0);
  }, [open]);

  // 1s tick while open, drives the expiry / resend countdowns.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const secondsLeft = otpSent ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const errMessage = (e, fallback) => e?.response?.data?.message || e?.data?.message || e?.message || fallback;

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    setOtpError("");
    setOtp("");
    try {
      const res = await onSendOtp();
      const t = Date.now();
      setNow(t);
      setExpiresAt(t + (res?.data?.expiresInSec ?? 60) * 1000);
      setResendAt(t + (res?.data?.resendAfterSec ?? 15) * 1000);
      setOtpSent(true);
      toast.success(res?.message || "OTP sent to admin email", { toastId: "admin-otp-sent" });
    } catch (e) {
      setOtpError(errMessage(e, "Failed to send OTP"));
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async () => {
    if (confirming) return;
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("Enter the 6-digit OTP sent to the admin email");
      return;
    }
    setConfirming(true);
    try {
      await onConfirm(otp);
    } catch (e) {
      const msg = errMessage(e, "Action failed");
      if (/otp/i.test(msg)) setOtpError(msg);
      else toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      maskClosable={false}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        !otpSent ? (
          <Button key="send" type="primary" danger={danger} loading={sending} onClick={handleSend}>
            Send OTP
          </Button>
        ) : (
          <Button
            key="confirm"
            type="primary"
            danger={danger}
            loading={confirming}
            disabled={otp.length !== 6 || secondsLeft === 0}
            onClick={handleConfirm}
          >
            {confirmText}
          </Button>
        ),
      ]}
    >
      {warning && (
        <div style={{ marginBottom: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
          {warning}
        </div>
      )}
      <div style={{ fontSize: 13, lineHeight: "22px" }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ wordBreak: "break-all" }}>
            <strong>{label}:</strong> {value}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {otpSent && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#444", marginBottom: 6 }}>
              Enter OTP <span style={{ color: "#dc2626" }}>*</span>
            </div>
            <Input
              autoFocus
              maxLength={6}
              inputMode="numeric"
              placeholder="6-digit code from email"
              value={otp}
              status={otpError ? "error" : undefined}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (otpError) setOtpError("");
              }}
              onPressEnter={() => otp.length === 6 && handleConfirm()}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 12 }}>
              <span style={{ color: secondsLeft === 0 ? "#dc2626" : "#666" }}>
                {secondsLeft === 0 ? "OTP expired — request a new one" : `Expires in ${secondsLeft}s`}
              </span>
              <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} disabled={resendIn > 0 || sending} loading={sending} onClick={handleSend}>
                {resendIn > 0 ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
              </Button>
            </div>
          </>
        )}
        {otpError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{otpError}</div>}
      </div>
    </Modal>
  );
};

export default AdminOtpModal;
